import { Fetcher } from 'hooks/fetch/Fetch';

export interface ProductFamily {
  id: string
  name: string
  area: string
}

export interface ProductFamilyResponse {
  families: ProductFamily[]
  areas: string[]
}

export interface CustomerProductAreaFamily {
  productAreaCode: string
  productFamilyCode: string
}

export interface ProductFamilyRepository {
  findAll(erpDataId: string): Promise<ProductFamilyResponse>
  findCustomerSelections(company: string, customerNo: string): Promise<CustomerProductAreaFamily[]>
  putCustomerSelections(company: string, customerNo: string, selections: CustomerProductAreaFamily[]): Promise<void>
}

interface ProductAreaFamilyApiResponse {
  erpDataId: string
  productAreaCode: string
  productAreaName: string
  productFamilyCode: string
  productFamilyName: string
}

export class InmemoryProductFamilyRepository implements ProductFamilyRepository {
  constructor(private readonly families: ProductFamily[]) {}

  async findAll(_erpDataId: string): Promise<ProductFamilyResponse> {
    return {
      families: this.families,
      areas: Array.from(new Set(this.families.map((family) => family.area))),
    };
  }

  async findCustomerSelections(_company: string, _customerNo: string): Promise<CustomerProductAreaFamily[]> {
    return [];
  }

  async putCustomerSelections(_company: string, _customerNo: string, _selections: CustomerProductAreaFamily[]): Promise<void> {
    return;
  }
}

export class HttpProductFamilyRepository implements ProductFamilyRepository {
  constructor(
    private readonly url: string,
    private readonly customerSelectionsUrl: string = '',
    private readonly fetch: Fetcher,
  ) {}

  async findAll(erpDataId: string): Promise<ProductFamilyResponse> {
    if (this.url === '') {
      throw new Error('Product family API URL is not configured');
    }

    const requestUrl = new URL(this.url);
    requestUrl.searchParams.set('erpDataId', erpDataId);

    const response = await this.fetch(requestUrl.toString(), { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Product family API request failed with status ${response.status}`);
    }

    const productAreaFamilies = await response.json() as ProductAreaFamilyApiResponse[];

    const families = productAreaFamilies.map((productAreaFamily) => ({
      id: `${productAreaFamily.productAreaCode}-${productAreaFamily.productFamilyCode}`,
      name: productAreaFamily.productFamilyName,
      area: productAreaFamily.productAreaName
      }));

    const areas = Array.from(new Set(families.map((family) => family.area)));

    return { families, areas };
  }

  async findCustomerSelections(company: string, customerNo: string): Promise<CustomerProductAreaFamily[]> {
    if (this.customerSelectionsUrl === '') {
      throw new Error('Customer product area family API URL is not configured');
    }

    const requestUrl = new URL(this.customerSelectionsUrl);
    requestUrl.searchParams.set('company', company);
    requestUrl.searchParams.set('customerNo', customerNo);

    const response = await this.fetch(requestUrl.toString(), { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Customer product area family API request failed with status ${response.status}`);
    }

    const selections = await response.json() as ProductAreaFamilyApiResponse[];
    return selections.map((selection) => ({
      productAreaCode: selection.productAreaCode,
      productFamilyCode: selection.productFamilyCode,
    }));
  }

  async putCustomerSelections(company: string, customerNo: string, selections: CustomerProductAreaFamily[]): Promise<void> {
    if (this.customerSelectionsUrl === '') {
      throw new Error('Customer product area family API URL is not configured');
    }

    const headers = new Headers({ 'Content-Type': 'application/json' });
    const requestUrl = new URL(this.customerSelectionsUrl);

    requestUrl.searchParams.set('company', company);
    requestUrl.searchParams.set('customerNo', customerNo);

    const response = await this.fetch(requestUrl.toString(), {
      method: 'PUT',
      headers,
      body: JSON.stringify(selections),
    });

    if (!response.ok) {
      throw new Error(`Customer product area family API update failed with status ${response.status}`);
    }
  }

}
